import type { ReactNode } from "react";
import { Loader2, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";

/* ── Column definition ──────────────────────────────────────── */

export interface DataTableColumn<T> {
  id: string;
  header: string;
  /** Render the cell content. Return a ReactNode. */
  cell: (row: T, index: number) => ReactNode;
  /** Header cell alignment — defaults to "left". */
  headerAlign?: "left" | "center" | "right";
  /** Cell alignment — defaults to "left". */
  align?: "left" | "center" | "right";
  /** Fixed width class, e.g. "w-12". */
  width?: string;
  /** If true, column only shows on sm+ breakpoints. */
  hideOnMobile?: boolean;
  /** Enable click-to-sort on this column. */
  sortKey?: string;
  /** Extra className on <th>. */
  headerClassName?: string;
  /** Extra className on <td>. */
  cellClassName?: string;
}

/* ── Props ─────────────────────────────────────────────────── */

interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  data: T[];
  /** Unique key extractor for each row. */
  rowKey: (row: T) => string;
  loading?: boolean;
  /** Content shown when data is empty (should use EmptyState). */
  emptyState?: ReactNode;
  /** Currently sorted column key. */
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSort?: (key: string) => void;
  /** Selection support. */
  selectable?: boolean;
  selectedIds?: Set<string>;
  onSelectChange?: (ids: Set<string>) => void;
  /** If set, only these rows show a checkbox and participate in select-all. */
  rowSelectable?: (row: T) => boolean;
  /** Add serial-number column. */
  showSerialNumber?: boolean;
  /** 1-based offset for serial numbers (e.g. (currentPage-1)*pageSize). */
  serialNumberOffset?: number;
  /** Mobile card renderer. When provided, table is hidden on small screens. */
  mobileCard?: (row: T, index: number) => ReactNode;
  /** Additional className on the outer wrapper. */
  className?: string;
  /** Pagination node rendered below the table. */
  pagination?: ReactNode;
  /** Optional footer rendered inside the table card, below rows. */
  footer?: ReactNode;
}

/* ── Alignment helpers ─────────────────────────────────────── */

const alignClass = (a?: "left" | "center" | "right") =>
  a === "center" ? "text-center" : a === "right" ? "text-right" : "text-left";

/* ── Sort icon ─────────────────────────────────────────────── */

function SortIcon({ active, order }: { active: boolean; order?: "asc" | "desc" }) {
  if (!active) return <ArrowUpDown className="ml-1 h-3.5 w-3.5 opacity-40" />;
  return order === "asc" ? (
    <ArrowUp className="ml-1 h-3.5 w-3.5" />
  ) : (
    <ArrowDown className="ml-1 h-3.5 w-3.5" />
  );
}

/* ── Component ─────────────────────────────────────────────── */

export function DataTable<T>({
  columns,
  data,
  rowKey,
  loading = false,
  emptyState,
  sortBy,
  sortOrder,
  onSort,
  selectable = false,
  selectedIds,
  onSelectChange,
  rowSelectable,
  showSerialNumber = false,
  serialNumberOffset = 0,
  mobileCard,
  className,
  pagination,
  footer,
}: DataTableProps<T>) {
  const allIds = data.map(rowKey);
  const selectableIds = rowSelectable ? data.filter((r) => rowSelectable(r)).map(rowKey) : allIds;
  const allSelected =
    selectable && selectableIds.length > 0 && selectableIds.every((id) => selectedIds?.has(id));

  const toggleAll = () => {
    if (!onSelectChange) return;
    if (allSelected) {
      onSelectChange(new Set());
    } else {
      onSelectChange(new Set(selectableIds));
    }
  };

  const toggleOne = (id: string) => {
    if (!onSelectChange || !selectedIds) return;
    const row = data.find((r) => rowKey(r) === id);
    if (rowSelectable && row && !rowSelectable(row)) return;
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectChange(next);
  };

  /* ── Loading state ─────────────────────────────────────── */
  if (loading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center py-12" role="status">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" aria-hidden />
        <span className="sr-only">Loading</span>
      </div>
    );
  }

  /* ── Empty state ───────────────────────────────────────── */
  if (data.length === 0) {
    return <>{emptyState}</>;
  }

  /* ── Determine visible desktop columns ─────────────────── */
  const desktopCols = columns;

  return (
    <div className={cn("space-y-4", className)}>
      {/* ── Desktop / tablet table ─────────────────────────── */}
      <div
        className={cn(
          "overflow-x-auto rounded-lg border border-slate-200",
          mobileCard ? "hidden sm:block" : "block"
        )}
      >
        <table className="w-full border-collapse text-left text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50/80">
              {selectable && (
                <th className="w-10 px-3 py-3 text-center">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={toggleAll}
                    aria-label="Select all"
                  />
                </th>
              )}
              {showSerialNumber && (
                <th className="w-12 whitespace-nowrap px-3 py-3 text-center font-heading text-xs font-semibold uppercase tracking-wide text-slate-500">
                  S/N
                </th>
              )}
              {desktopCols.map((col) => {
                const isSortable = !!col.sortKey && !!onSort;
                return (
                  <th
                    key={col.id}
                    className={cn(
                      "whitespace-nowrap px-4 py-3 font-heading text-xs font-semibold uppercase tracking-wide text-slate-500",
                      alignClass(col.headerAlign ?? col.align),
                      isSortable && "cursor-pointer select-none hover:text-slate-700",
                      col.width,
                      col.headerClassName
                    )}
                    onClick={isSortable ? () => onSort!(col.sortKey!) : undefined}
                  >
                    <span className="inline-flex items-center">
                      {col.header}
                      {isSortable && (
                        <SortIcon
                          active={sortBy === col.sortKey}
                          order={sortBy === col.sortKey ? sortOrder : undefined}
                        />
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {data.map((row, idx) => {
              const id = rowKey(row);
              const isSelected = selectedIds?.has(id);
              return (
                <tr
                  key={id}
                  className={cn(
                    "hover:bg-slate-50/60 transition-colors",
                    isSelected && "bg-primary/5"
                  )}
                >
                  {selectable && (
                    <td className="px-3 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                      {rowSelectable && !rowSelectable(row) ? (
                        <span className="inline-block w-4 text-slate-300">—</span>
                      ) : (
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleOne(id)}
                          aria-label={`Select row ${idx + 1}`}
                        />
                      )}
                    </td>
                  )}
                  {showSerialNumber && (
                    <td className="whitespace-nowrap px-3 py-3 text-center tabular-nums text-slate-400">
                      {serialNumberOffset + idx + 1}
                    </td>
                  )}
                  {desktopCols.map((col) => (
                    <td
                      key={col.id}
                      className={cn(
                        "px-4 py-3",
                        alignClass(col.align),
                        col.width,
                        col.cellClassName
                      )}
                    >
                      {col.cell(row, idx)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Mobile cards ─────────────────────────────────────── */}
      {mobileCard && (
        <ul className="space-y-3 sm:hidden" role="list">
          {data.map((row, idx) => (
            <li key={rowKey(row)}>{mobileCard(row, idx)}</li>
          ))}
        </ul>
      )}

      {/* ── Footer ───────────────────────────────────────────── */}
      {footer}

      {/* ── Pagination ───────────────────────────────────────── */}
      {pagination}
    </div>
  );
}
